import { styleSizesNumber } from 'constants/styleSizes';
import { useLayoutEffect } from 'react';

let lockCount = 0;
let releaseBody: (() => void) | undefined;

export const scrollWindowWithoutAnimation = (x: number, y: number) => {
  const root = document.documentElement;
  const value = root.style.getPropertyValue('scroll-behavior');
  const priority = root.style.getPropertyPriority('scroll-behavior');
  root.style.setProperty('scroll-behavior', 'auto', 'important');
  window.scrollTo(x, y);
  if (value) root.style.setProperty('scroll-behavior', value, priority);
  else root.style.removeProperty('scroll-behavior');
};

const acquireBodyLock = () => {
  if (lockCount === 0) {
    const body = document.body;
    const x = window.scrollX;
    const y = window.scrollY;
    const alreadyFixed = window.getComputedStyle(body).position === 'fixed';
    // Leave overflow/touch-action to their existing owners, including nested modals.
    const values: { [property: string]: string } = alreadyFixed ? {} : {
      position: 'fixed', top: `${-y}px`, left: `${-x}px`, width: '100%',
    };
    const previous = Object.keys(values).map(property => ({
      property, value: body.style.getPropertyValue(property), priority: body.style.getPropertyPriority(property),
    }));
    Object.keys(values).forEach(property => body.style.setProperty(property, values[property]));
    releaseBody = () => {
      const ownsPosition = !alreadyFixed && body.style.position === 'fixed' && body.style.top === values.top;
      previous.forEach(({ property, value, priority }) => {
        // Do not overwrite a lock or style installed by another owner meanwhile.
        if (body.style.getPropertyValue(property) === values[property] && !body.style.getPropertyPriority(property)) {
          if (value) body.style.setProperty(property, value, priority);
          else body.style.removeProperty(property);
        }
      });
      if (ownsPosition) scrollWindowWithoutAnimation(x, y);
    };
  }
  lockCount += 1;
  return () => {
    lockCount -= 1;
    if (lockCount === 0) {
      releaseBody?.();
      releaseBody = undefined;
    }
  };
};

/**
 * Locks or unlocks scrolling on the document body on mobile screens only.
 * Useful for modals or mobile bottom sheets.
 *
 * @param isLocked - When true, scrolling on the page is disabled.
 */
export const useLockBodyScroll = (isLocked: boolean) => {
  useLayoutEffect(() => {
    const media = window.matchMedia(`(max-width: ${styleSizesNumber.miniBreakpoint}px)`);
    let release: (() => void) | undefined;
    const update = () => {
      if (isLocked && media.matches && !release) release = acquireBodyLock();
      else if ((!isLocked || !media.matches) && release) {
        release();
        release = undefined;
      }
    };
    update();
    media.addListener(update);
    return () => {
      media.removeListener(update);
      release?.();
    };
  }, [isLocked]);
};
